import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { sortDatasets, bytesToViewString } from '../lib/dataTransforms';

const route = window._env_.REACT_APP_WCIBAPI_URL || '';

// Define our single API slice object
export const apiSlice = createApi({
    reducerPath: 'api',
    baseQuery: fetchBaseQuery({ baseUrl: `${route}` }),
    // 'Datasets' here is a coarse tag — invalidating it refetches the
    // entire dataset list when ANY dataset is changed. Fine for the
    // current portal scale (≤ a few hundred datasets per user) but worth
    // splitting into per-id tags if the catalog grows.
    tagTypes: ['Datasets', 'Roles', 'MQTTStatus'],
    endpoints: builder => ({
        getDatasets: builder.query({
            query: () => {
                return { url: '/v1/dataset', credentials: 'include' };
            },
            transformResponse: responseData => {
                const datasets = { raw: [], filtered: [] };
                for (const ds of responseData.Datasets) {
                    const ar = ds.AccessRequest;
                    if (ds.AccessRequest.Note) {
                        ar.CreateDate = new Date(ds.AccessRequest.CreateDate);
                    }
                    datasets.raw.push({
                        ...ds,
                        CreateDate: new Date(ds.CreateDate),
                        AccessRequest: ar
                    });
                }
                datasets.filtered = sortDatasets(datasets.raw.filter(ds => ds.AccessRole !== 'none' || ds.AccessType === 'open'), 'asc', 'AccessRole');
                return datasets;
            },
            providesTags: ['Datasets']
        }),
        getRoles: builder.query({
            query: id => {
                return { url: `/v1/dataset/${id}/roles`, credentials: 'include' };
            },
            providesTags: (result, err, arg) => [{ type: 'Roles', id: arg }]
        }),
        getAccessRequests: builder.query({
            query: id => {
                return { url: `/v1/dataset/${id}/accessRequests`, credentials: 'include' };
            },
            transformResponse: responseData => responseData.AccessRequests.map(accReq => {
                return { ...accReq, CreateDate: new Date(accReq.CreateDate) };
            }),
            providesTags: (result, err, arg) => [{ type: 'AccessRequests', id: arg }]
        }),
        acceptAccessRequest: builder.mutation({
            query: userToAdd => ({
                url: `/v1/dataset/${userToAdd.datasetid}/roles/${userToAdd.user}/user?sendemail=true`,
                credentials: 'include',
                method: 'POST'
            }),
            invalidatesTags: (result, err, arg) => [
                { type: 'Roles', id: arg.datasetid },
                { type: 'AccessRequests', id: arg.datasetid },
            ]
        }),
        rejectAccessRequest: builder.mutation({
            query: accReqToDelete => ({
                url: `/v1/dataset/${accReqToDelete.datasetid}/accessRequests/${accReqToDelete.user}?sendemail=true`,
                credentials: 'include',
                method: 'DELETE'
            }),
            invalidatesTags: (result, err, arg) => [{ type: 'AccessRequests', id: arg.datasetid }]
        }),
        getFiles: builder.query({
            query: ({ id, params }) => {
                const qp = [];

                for (const [par, val] of Object.entries(params)) {
                    if (val !== undefined) {
                        qp.push(`${par}=${encodeURIComponent(val)}`);
                    }
                }

                return {
                    url: `/v1/dataset/${id}/files?` + qp.join('&'),
                    credentials: 'include'
                };
            },
            transformResponse: responseData => {
                return {
                    pagination: responseData.pagination,
                    files: responseData.data.map(file => {
                        return { ...file, FileSize: bytesToViewString(file.FileSize) };
                    })
                };
            }
        }),
        updateDataset: builder.mutation({
            query: data => ({
                url: `/v1/dataset/${data.datasetid}`,
                credentials: 'include',
                method: 'PATCH',
                body: data.metadata
            }),
            invalidatesTags: ['Datasets']
        }),
        postRequestAccess: builder.mutation({
            query: data => ({
                url: `/v1/dataset/${data.datasetid}/requestAccess?sendemail=true`,
                credentials: 'include',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['Datasets']
        }),
        deleteRequestAccess: builder.mutation({
            query: data => ({
                url: `/v1/dataset/${data.datasetid}/requestAccess`,
                credentials: 'include',
                method: 'DELETE',
                body: data
            }),
            invalidatesTags: ['Datasets']
        }),
        addRole: builder.mutation({
            query: data => {
                return {
                    url: `/v1/dataset/${data.datasetid}/roles/${data.user}/${data.role}?sendemail=true`,
                    credentials: 'include',
                    method: 'POST'
                };
            },
            invalidatesTags: (result, err, arg) => [
                { type: 'Roles', id: arg.datasetid },
                { type: 'AccessRequests', id: arg.datasetid }
            ]
        }),
        removeRole: builder.mutation({
            query: (data) => ({
                url: `/v1/dataset/${data.datasetid}/roles/${data.user}/${data.role}`,
                credentials: 'include',
                method: 'DELETE'
            }),
            invalidatesTags: (result, err, arg) => [{ type: 'Roles', id: arg.datasetid }]
        }),
        createDataset: builder.mutation({
            // Argument shape: { body, idempotencyKey? }. Pass body alone
            // for back-compat with any caller that hasn't been updated.
            query: (arg) => {
                const { body, idempotencyKey } = (arg && typeof arg === 'object' && 'body' in arg)
                    ? arg
                    : { body: arg, idempotencyKey: undefined };
                return {
                    url: '/v1/dataset',
                    credentials: 'include',
                    method: 'POST',
                    body,
                    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined
                };
            },
            invalidatesTags: ['Datasets']
        }),
        getMqttStatus: builder.query({
            query: (datasetid) => ({
                url: `/v1/dataset/${datasetid}/mqtt/status`,
                credentials: 'include'
            }),
            providesTags: (result, err, arg) => [{ type: 'MQTTStatus', id: arg }]
        }),
        createMqttSetup: builder.mutation({
            query: ({ datasetid, body }) => ({
                url: `/v1/dataset/${datasetid}/mqtt/setup`,
                credentials: 'include',
                method: 'POST',
                body: body || {}
            }),
            invalidatesTags: (result, err, arg) => [{ type: 'MQTTStatus', id: arg.datasetid }]
        }),
        updateMqttSetup: builder.mutation({
            query: ({ datasetid, body }) => ({
                url: `/v1/dataset/${datasetid}/mqtt/setup`,
                credentials: 'include',
                method: 'PATCH',
                body: body || {}
            }),
            invalidatesTags: (result, err, arg) => [{ type: 'MQTTStatus', id: arg.datasetid }]
        }),
        deleteMqttSetup: builder.mutation({
            query: ({ datasetid }) => ({
                url: `/v1/dataset/${datasetid}/mqtt/setup`,
                credentials: 'include',
                method: 'DELETE'
            }),
            invalidatesTags: (result, err, arg) => [{ type: 'MQTTStatus', id: arg.datasetid }]
        }),
    })
});

// Export the auto-generated hook for the `getPosts` query endpoint
export const {
    useGetDatasetsQuery,
    useGetRolesQuery,
    useGetAccessRequestsQuery,
    useAcceptAccessRequestMutation,
    useRejectAccessRequestMutation,
    useGetFilesQuery,
    useUpdateDatasetMutation,
    usePostRequestAccessMutation,
    useDeleteRequestAccessMutation,
    useAddRoleMutation,
    useRemoveRoleMutation,
    useCreateDatasetMutation,
    useGetMqttStatusQuery,
    useCreateMqttSetupMutation,
    useUpdateMqttSetupMutation,
    useDeleteMqttSetupMutation
} = apiSlice;
